import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, Headphones, Mic, Volume2 } from 'lucide-react';
import * as tv from '../../lib/twilioVoice.js';

const read = (key) => { try { return localStorage.getItem(key) || ''; } catch { return ''; } };
const write = (key, val) => { try { localStorage.setItem(key, val); } catch { /* storage blocked */ } };

/**
 * Live input-level bar for the chosen microphone. Opens its own stream only
 * while mounted (i.e. while the Audio Settings panel is expanded).
 * @param {{deviceId: string}} props
 */
function MicMeter({ deviceId }) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    let raf; let stream; let ctx; let stopped = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId && deviceId !== 'default' ? { deviceId: { exact: deviceId } } : true,
        });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        const Ctx = window.AudioContext || window.webkitAudioContext;
        ctx = new Ctx();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let peak = 0;
          for (let i = 0; i < data.length; i += 1) peak = Math.max(peak, Math.abs(data[i] - 128));
          setLevel(Math.min(100, Math.round((peak / 128) * 160)));
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch { /* permission denied: bar stays empty */ }
    })();
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      ctx?.close?.();
    };
  }, [deviceId]);

  return (
    <div className="h-1.5 rounded-full bg-civix-border overflow-hidden" role="meter" aria-label="Microphone level" aria-valuenow={level}>
      <div className={`h-full transition-[width] duration-75 ${level > 8 ? 'bg-emerald-400' : 'bg-white/20'}`} style={{ width: `${level}%` }} />
    </div>
  );
}

/**
 * Collapsible microphone / speaker pickers for the softphone. Selections are
 * saved to localStorage and re-applied whenever the voice device is ready.
 * @returns {JSX.Element}
 */
export default function AudioSettingsSection() {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState({ inputs: [], outputs: [], outputSupported: true });
  const [input, setInput] = useState(() => read(tv.PREFERRED_INPUT_KEY));
  const [output, setOutput] = useState(() => read(tv.PREFERRED_OUTPUT_KEY));
  const [blocked, setBlocked] = useState(false);
  const askedRef = useRef(false);

  const refresh = useCallback(async (requestPermission = false) => {
    try {
      const found = await tv.getAudioDevices({ requestPermission });
      setDevices(found);
      // Labels are blank without permission: treat that as "not granted yet".
      setBlocked(found.inputs.length > 0 && found.inputs.every((d) => !d.label));
    } catch {
      setDevices({ inputs: [], outputs: [], outputSupported: false });
    }
  }, []);

  // Apply saved devices on mount and again whenever the device list changes
  // (e.g. a headset is plugged in).
  useEffect(() => {
    tv.applySavedAudioDevices();
    refresh(false);
    const md = navigator.mediaDevices;
    const onChange = () => { refresh(false); tv.applySavedAudioDevices(); };
    md?.addEventListener?.('devicechange', onChange);
    return () => md?.removeEventListener?.('devicechange', onChange);
  }, [refresh]);

  useEffect(() => {
    if (open && !askedRef.current) { askedRef.current = true; refresh(true); }
  }, [open, refresh]);

  async function pickInput(id) {
    setInput(id);
    write(tv.PREFERRED_INPUT_KEY, id);
    if (!(await tv.setAudioInputDevice(id))) toast('Microphone saved. It will be used when the softphone is ready.', { icon: 'ℹ️' });
  }

  async function pickOutput(id) {
    setOutput(id);
    write(tv.PREFERRED_OUTPUT_KEY, id);
    if (!(await tv.setAudioOutputDevice(id))) toast('Speaker saved. It will be used when the softphone is ready.', { icon: 'ℹ️' });
  }

  const label = (d, i, kind) => d.label || `${kind} ${i + 1}`;
  const selectCls = 'w-full appearance-none bg-civix-panel border border-civix-border rounded-lg pl-3 pr-8 py-2 text-xs text-white focus:outline-hidden focus:border-civix-purple';

  return (
    <div className="rounded-xl border border-civix-border bg-civix-panelLight/60" data-testid="audio-settings">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-xs text-white/70 hover:text-white"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2"><Headphones className="w-4 h-4 text-civix-purpleSoft" /> Audio Settings</span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {blocked && (
            <p className="text-[11px] text-amber-300/90">
              Allow microphone access in your browser to see device names.
            </p>
          )}

          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40" htmlFor="audio-input">
              <Mic className="w-3 h-3" /> Microphone
            </label>
            <div className="relative">
              <select id="audio-input" value={input} onChange={(e) => pickInput(e.target.value)} className={selectCls}>
                <option value="">System default</option>
                {devices.inputs.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{label(d, i, 'Microphone')}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <MicMeter deviceId={input} />
          </div>

          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40" htmlFor="audio-output">
              <Volume2 className="w-3 h-3" /> Speaker / Headset
            </label>
            {devices.outputSupported ? (
              <div className="relative">
                <select id="audio-output" value={output} onChange={(e) => pickOutput(e.target.value)} className={selectCls}>
                  <option value="">System default</option>
                  {devices.outputs.map((d, i) => <option key={d.deviceId} value={d.deviceId}>{label(d, i, 'Speaker')}</option>)}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            ) : (
              <p className="text-[11px] text-white/45">
                Your browser can&apos;t switch the speaker. Use your system sound settings (Chrome and Edge support this).
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
